/**
 * ReadinessOverview Component
 *
 * Hero section showing overall AEO readiness score with circular progress
 * and blocker summary.
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

interface Blocker {
  severity: 'critical' | 'high' | 'medium';
  message: string;
  dimension: string;
}

interface ReadinessOverviewProps {
  overallScore: number;
  status: 'ready' | 'mostly_ready' | 'needs_work' | 'not_ready';
  blockers: Blocker[];
}

const statusConfig = {
  ready: {
    label: 'Ready',
    color: 'bg-green-500',
    textColor: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    icon: CheckCircle2
  },
  mostly_ready: {
    label: 'Mostly Ready',
    color: 'bg-blue-500',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    icon: CheckCircle2
  },
  needs_work: {
    label: 'Needs Work',
    color: 'bg-yellow-500',
    textColor: 'text-yellow-600',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950/20',
    icon: AlertTriangle
  },
  not_ready: {
    label: 'Not Ready',
    color: 'bg-red-500',
    textColor: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-950/20',
    icon: XCircle
  }
};

export function ReadinessOverview({ overallScore, status, blockers }: ReadinessOverviewProps) {
  const config = statusConfig[status];
  const StatusIcon = config.icon;
  const criticalBlockers = blockers.filter(b => b.severity === 'critical');

  // Calculate the stroke dasharray for the circular progress
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (overallScore / 100) * circumference;

  return (
    <Card className={config.bgColor}>
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Circular Progress */}
          <div className="relative w-32 h-32 flex-shrink-0">
            <svg className="w-32 h-32 transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200 dark:text-gray-700"
              />
              {/* Progress circle */}
              <circle
                cx="64"
                cy="64"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                className={config.textColor}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset,
                  transition: 'stroke-dashoffset 0.5s ease-in-out'
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold">{overallScore}</span>
              <span className="text-sm text-muted-foreground">/100</span>
            </div>
          </div>

          {/* Status and Blockers */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <StatusIcon className={`h-5 w-5 ${config.textColor}`} />
              <Badge variant="outline" className={`${config.textColor} border-current`}>
                {config.label}
              </Badge>
            </div>

            {criticalBlockers.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground flex items-center gap-1 justify-center md:justify-start">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  {criticalBlockers.length} blocker{criticalBlockers.length !== 1 ? 's' : ''} preventing AEO generation
                </p>
                <ul className="text-sm space-y-1">
                  {criticalBlockers.slice(0, 3).map((blocker, idx) => (
                    <li key={idx} className="text-muted-foreground">
                      {blocker.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {status === 'ready'
                  ? 'All systems go! Ready for AEO content generation.'
                  : 'No critical blockers. Continue improving readiness score.'}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
